import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthService, RoutingService } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, pairwise, switchMap, withLatestFrom } from 'rxjs/operators';
import { CartActions } from '../../../../features/cart/base/store/actions';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageService, GlobalMessageType } from '../../../global-message';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { PunchOutConnector } from '../../connectors/punch-out-connector';
import { UserActions } from '../actions';
import { LoadPunchOutDataReset } from '../actions/punch-out-data-action';
import { PunchOutFacade } from '../../facades';

@Injectable()
export class PunchOutEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  logoutPunchOut$: Observable<AppActions.NoOpAction> = createEffect(() =>
    // We can't listen to Logout action here because the punchout data may have already been removed by then
    this.authService.logoutInProgress$.pipe(
      distinctUntilChanged(),
      withLatestFrom(this.punchOutService.getPunchOutEntity()),
      pairwise(),
      // Wait until logout is done (logoutInProgress: true -> false) and logout from punchout
      map(([[oldLogoutInProgress, oldPunchOutData], [newLogoutInProgress, newPunchOutData]]) => {
        const punchOutData = newPunchOutData ?? oldPunchOutData;
        if (Boolean(punchOutData) && oldLogoutInProgress === true && newLogoutInProgress === false) {
          return punchOutData;
        }
      }),
      filter(Boolean),
      map((punchOutData) => {
        this.punchOutService.logOut(punchOutData);
        return new AppActions.NoOpAction();
      })
    )
  );

  loadPunchOutData$: Observable<UserActions.PunchOutDataActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.PunchOutDataActionTypes.LoadPunchOutData),
      switchMap((action: UserActions.LoadPunchOutData) =>
        this.connector.load(action.payload).pipe(
          map((res) => new UserActions.LoadPunchOutDataSuccess(res)),
          catchError((error) => of(new UserActions.LoadPunchOutDataFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForLoadPunchOutData$: Observable<AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.PunchOutDataActionTypes.LoadPunchOutDataFail),
      map(() => {
        this.authService.coreLogout().finally(() => {
          this.routingService.go({ cxRoute: 'login' });
          // Could not get it to work with actions directly
          this.globalMessageService.add(
            {
              key: 'checkout.punchout.sessionSetupFailure_message',
            },
            GlobalMessageType.MSG_TYPE_ERROR,
            30000
          );
        });
        return new AppActions.NoOpAction();
      })
    )
  );

  punchOut$: Observable<
    UserActions.PunchOutSuccess | UserActions.PunchOutFail | CartActions.ClearCartsState | LoadPunchOutDataReset
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.PunchOutActionTypes.PunchOut),
      switchMap((action: UserActions.PunchOut) =>
        this.connector.punchOut(action.payload, action.userId).pipe(
          mergeMap(() => {
            return [new UserActions.PunchOutSuccess(), new CartActions.ClearCartsState(), new LoadPunchOutDataReset()];
          }),
          catchError((error) => of(new UserActions.PunchOutFail(normalizeHttpError(error))))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private connector: PunchOutConnector,
    private authService: AuthService,
    private routingService: RoutingService,
    private globalMessageService: GlobalMessageService,
    private punchOutService: PunchOutFacade
  ) {}
}
